import { mapGetters, mapActions } from 'vuex'
import { get } from 'lodash'

import { effectiveLimit, overagePercentage } from 'app/utils/subscription'

const BANDWIDTH_INFINITY = -1
const BYTES_TO_GIGABYTES_FACTOR = Number(FONTAWESOME.BANDWIDTH_GIGABYTE_MULTIPLIER)

// A threshold the progress bar can never cross, for the cases where there is no
// overage band to be in danger of using up. `dangerPercentage` is compared
// against a 0-100 progress value (`components-wa/usage/ProgressBar.vue`), so
// anything above 100 means "never".
const NEVER_DANGER_PERCENTAGE = 101

export const BandwidthUsageMixin = {
  data() {
    return {
      hasBandwidthFetched: false,
      // TODO: Metered Billing full release: remove
      isMeteredBillingBandwidthPartiallyReleased: false,
      bandwidthFetchFailed: false,
      meteredBandwidthUnits: 'GB'
    }
  },

  computed: {
    ...mapGetters('meteredBilling', ['meteredBilling', 'meteredBillingSettings']),
    ...mapGetters('tokens', ['npmToken', 'bandwidthUsed']),

    atBandwidthPlanLimit() {
      return this.totalBandwidthUsedInGigabytes >= this.bandwidthLimit
    },

    atBandwidthUsageLimit() {
      // if metered bandwidth limit is set to none (-1) then user would not be at the limit so we return false
      if (this.meteredBillingMaxMonthlyBandwidth === -1) return false

      return this.totalBandwidthUsedInGigabytes >= this.meteredBillingMaxMonthlyBandwidth
    },

    bandwidthDollarLimit() {
      return this.meteredBillingSettings?.maxMonthlyBandwidthMoneyLimit > 0 ? this.meteredBillingSettings?.maxMonthlyBandwidthMoneyLimit / 100 : 0
    },

    // The pre-paid limit in bytes, or `null` when there is no ceiling to exceed.
    //
    // Every overage figure below is `usage − this`, so it has to be computed in
    // one place: `bandwidthLimit` carries the shared `-1` unlimited sentinel and
    // is `0` on a CDN-only plan, and multiplying either straight through gives a
    // negative or zero byte threshold — which reports the customer's entire
    // usage as overage.
    bandwidthLimitBytes() {
      if (this.bandwidthLimit === BANDWIDTH_INFINITY) return null

      return Math.max(this.bandwidthLimit, 0) * BYTES_TO_GIGABYTES_FACTOR
    },

    bandwidthOverage() {
      if (this.bandwidthLimitBytes === null) return 0

      return Math.max(this.totalBandwidthUsed - this.bandwidthLimitBytes, 0)
    },

    billableBandwidthOverageBytes() {
      // Nothing to bill above a limit that has no ceiling.
      if (this.bandwidthLimitBytes === null) return 0

      const bandwidthLimitBytes = this.bandwidthLimitBytes

      const actualOverageBytes = Math.max(this.totalBandwidthUsed - bandwidthLimitBytes, 0)

      const overageBandwidthIncrementGigabytes = this.$subscription?.plan?.overageBandwidthIncrement || 0

      const isOverageLessThanOneIncrement = actualOverageBytes < overageBandwidthIncrementGigabytes * BYTES_TO_GIGABYTES_FACTOR

      if (isOverageLessThanOneIncrement) {
        // We don't bill for these small overages.
        return 0
      }

      if (this.isMeteredBillingBandwidthCustomerEnabled && this.hasInfiniteBandwidth) {
        // If bandwidth is metered and unlimited, then overage billing is based
        // simply on the amount of bytes over the plan allownace, with no capping
        // to a limit, since there is no limit -- unless it's merely a toe over the line.
        return actualOverageBytes
      }

      const maxMonthlyBandwidthBytes = this.meteredBillingMaxMonthlyBandwidth * BYTES_TO_GIGABYTES_FACTOR

      const isMeteredBillingLimitedWithExcess = this.isMeteredBillingBandwidthCustomerEnabled && this.totalBandwidthUsed >= maxMonthlyBandwidthBytes

      if (isMeteredBillingLimitedWithExcess) {
        // cap billable overages at the max monthly, which is the limit set by the customer
        return Math.max(maxMonthlyBandwidthBytes - bandwidthLimitBytes, 0)
      }

      return actualOverageBytes
    },

    bandwidthOverageToGigabytes() {
      return this.bandwidthOverage / BYTES_TO_GIGABYTES_FACTOR
    },

    // The size of the overage band: how many gigabytes past the pre-paid limit
    // the customer's metered-billing cap allows, or `BANDWIDTH_INFINITY` when
    // they've set no cap.
    bandwidthUsageOverageLimit() {
      if (this.hasInfiniteBandwidth) return BANDWIDTH_INFINITY

      return this.meteredBillingMaxMonthlyBandwidth - this.bandwidthLimit
    },

    bandwidthUsageOveragePercentage() {
      const overage = this.bandwidthOverageToGigabytes > 0 ? this.bandwidthOverageToGigabytes : 0

      return overagePercentage(overage, this.bandwidthUsageOverageLimit)
    },

    // Danger colour once the overage band is used up — but only when there is a
    // band to use up.
    //
    // `bandwidthUsageOverageLimit` is not always a real size: `BANDWIDTH_INFINITY`
    // when the customer set no cap, `0` when the cap equals their limit, and
    // negative once an add-on raises the limit past the cap. A bare truthiness
    // test read `-1` and `-40` as real bands and went danger at 100% of a band
    // that doesn't exist, while the old `bandwidthLimit * 10` fallback turned a
    // 0 GB limit into a threshold of 0 — i.e. danger always.
    bandwidthOverageDangerPercentage() {
      return this.bandwidthUsageOverageLimit > 0 ? 100 : NEVER_DANGER_PERCENTAGE
    },

    hasSetBandwidthBillingLimit() {
      return this.meteredBillingSettings?.maxMonthlyBandwidthMoneyLimit > 0
    },

    hasInfiniteBandwidth() {
      return this.meteredBillingMaxMonthlyBandwidth === BANDWIDTH_INFINITY
    },

    isMeteredBillingBandwidthFeatureOn() {
      return FONTAWESOME.METERED_BANDWIDTH_ACTIVE === 'yes'
    },

    isMeteredBillingBandwidthCustomerEnabled() {
      return this.meteredBillingSettings?.meteredBandwidthOn === true
    },

    isMeteredBillingBandwidth() {
      return this.isMeteredBillingBandwidthFeatureOn && this.isMeteredBillingBandwidthCustomerEnabled
    },

    meteredBillingMaxMonthlyBandwidth() {
      return get(this.meteredBillingSettings, ['maxMonthlyBandwidth'], -1)
    },

    planBandwidthOverageUnitPrice() {
      return this.$subscription?.plan?.overageBandwidthPrice?.amount
    },

    // Bandwidth the customer actually has: the plan's allowance plus any
    // purchased add-ons. Overage bills against this, so usage counters and
    // warning thresholds must measure against it too.
    bandwidthLimit() {
      return effectiveLimit(this.$subscription, 'monthlyBandwidth') || 0
    },

    // The plan's own allowance, add-ons excluded — for copy that describes what
    // the tier includes ("Your Pro plan comes with N GB of bandwidth").
    planBandwidthUsageLimit() {
      return get(this.$subscription, ['plan', 'monthlyBandwidth'], 0)
    },

    // How far through the pre-paid limit the customer is. Guarded, because this
    // drives the 60/90/100 thresholds the usage warnings key off: dividing by a
    // `0` limit (CDN-only plans) gave `Infinity` and tripped every warning, and
    // dividing by the `-1` unlimited sentinel gave a negative that suppressed
    // all of them.
    planBandwidthUsagePercentage() {
      return overagePercentage(this.totalBandwidthUsedInGigabytes, this.bandwidthLimit)
    },

    planHasAccessToBandwidthMeteredBilling() {
      return this.$subscription?.plan?.meteredBillingBandwidthEnabled
    },

    totalBandwidthUsed() {
      const bandwidthObject = this.bandwidthUsed
      const bandwidth = get(bandwidthObject, ['bandwidth'], 0)

      return bandwidth
    },

    totalBandwidthUsedInGigabytes() {
      return this.totalBandwidthUsed / BYTES_TO_GIGABYTES_FACTOR
    },

    totalBandwidthOverageCost() {
      if (!this.$subscription) {
        // If the $subscription shortcut has not yet been resolved, don't
        // divide by zero--return early. This will re-run when this.$subscription
        // is ready.
        return 0
      }

      if (!this.$subscription.plan.privateRegistriesEnabled) {
        // When the plan doesn't support bandwidth.
        return 0
      }

      const pricePerOverageIncrementCents = this.$subscription.plan.overageBandwidthPrice.amount || 0
      const overageBandwidthIncrement = this.$subscription.plan.overageBandwidthIncrement
      if (overageBandwidthIncrement <= 0) {
        // Again, let's return 0 instead of possibly dividing by a non-sense number, just in case.
        return 0
      }

      const bandwidthOverageGigabytes = this.billableBandwidthOverageBytes / BYTES_TO_GIGABYTES_FACTOR

      const additionalIncrements = bandwidthOverageGigabytes / overageBandwidthIncrement

      // We don't bill them until they've used at least one whole increment of usage
      return additionalIncrements >= 1 ? pricePerOverageIncrementCents * additionalIncrements : 0
    },

    npmTokenDeactivated() {
      if (this.npmToken) return this.npmToken.active !== true

      return false
    }
  },

  async created() {
    if (!this.$subscription) return

    if (this.$subscription.meteredBilling?.id) {
      try {
        await this.fetchMeteredBilling()
      } catch (e) {
        // Non-fatal. Roles other than owner, admin, and billing are refused this
        // endpoint, and meteredBillingSettings falls back to the subscription's
        // copy of the record. Usage still has to be fetched below: banners read
        // usage and settings together, so half-loaded state misreports service.
      }
    }

    try {
      await this.fetchTokenUsage()

      this.hasBandwidthFetched = true
    } catch (e) {
      this.bandwidthFetchFailed = true
    }
  },

  methods: {
    ...mapActions('meteredBilling', ['fetchMeteredBilling']),

    ...mapActions('tokens', ['fetchTokenUsage'])
  }
}
